import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Button,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  AddBusiness as AddBusinessIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';

interface AssociatedCustomer {
  id: string;
  displayName: string;
  companyName?: string;
  givenName?: string;
  familyName?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  balance?: number;
  currencyCode?: string;
  isActive?: boolean;
  billingAddress?: {
    id?: string;
    line1?: string;
    line2?: string;
    line3?: string;
    line4?: string;
    line5?: string;
    city?: string;
    country?: string;
    countrySubDivisionCode?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
  } | null;
  associatedAt?: string;
}

export const AddVendorBusinessPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState<string>('');
  const [associatedCustomers, setAssociatedCustomers] = useState<AssociatedCustomer[]>([]);

  // Form state
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [product, setProduct] = useState<string>('');
  const [itemDescription, setItemDescription] = useState<string>('');
  const [itemTotalPrice, setItemTotalPrice] = useState<string>('');
  const [subsidiaryInvoiceNumber, setSubsidiaryInvoiceNumber] = useState<string>('');
  const [subsidiaryInvoiceAmount, setSubsidiaryInvoiceAmount] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  // Create customer modal state
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [customerFormSubmitting, setCustomerFormSubmitting] = useState(false);
  const [customerFormError, setCustomerFormError] = useState<string | null>(null);
  const [customerValidationError, setCustomerValidationError] = useState<string | null>(null);
  const [isDuplicateNameError, setIsDuplicateNameError] = useState(false);
  const [refreshingCustomers, setRefreshingCustomers] = useState(false);

  // Customer form state
  const [displayName, setDisplayName] = useState<string>('');
  const [givenName, setGivenName] = useState<string>('');
  const [middleName, setMiddleName] = useState<string>('');
  const [familyName, setFamilyName] = useState<string>('');
  const [suffix, setSuffix] = useState<string>('');
  const [primaryPhone, setPrimaryPhone] = useState<string>('');
  const [mobilePhone, setMobilePhone] = useState<string>('');
  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [website, setWebsite] = useState<string>('');
  const [billAddrLine1, setBillAddrLine1] = useState<string>('');
  const [billAddrLine2, setBillAddrLine2] = useState<string>('');
  const [billAddrCity, setBillAddrCity] = useState<string>('');
  const [billAddrCountry, setBillAddrCountry] = useState<string>('');
  const [billAddrCountrySubDivisionCode, setBillAddrCountrySubDivisionCode] = useState<string>('');
  const [billAddrPostalCode, setBillAddrPostalCode] = useState<string>('');
  const [currency, setCurrency] = useState<string>('USD');

  const currencies = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'JPY', 'CNY', 'INR'];

  useEffect(() => {
    const fetchVendorDetails = async () => {
      if (!vendorId) return;

      setLoading(true);
      setError(null);
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/accounting/admin/vendors/${vendorId}`,
          {
            timeout: 30000 // 30 seconds timeout
          }
        );

        if (!response || !response.ok) {
          if (response?.status === 403) {
            throw new Error('Access denied. You do not have permission to view this vendor.');
          }
          if (response?.status === 404) {
            throw new Error('Vendor not found');
          }
          throw new Error('Failed to fetch vendor details');
        }

        const result = await response.json();
        setVendorName(result.data.displayName);
        setAssociatedCustomers(result.data.associatedCustomers || []);
      } catch (err: any) {
        console.error('Error fetching vendor details:', err);
        setError(err.message || 'Failed to fetch vendor details');
      } finally {
        setLoading(false);
      }
    };

    fetchVendorDetails();
  }, [vendorId]);

  const fetchVendorDetailsForRefresh = async () => {
    if (!vendorId) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error('Failed to fetch vendor details');
      }

      const result = await response.json();
      setAssociatedCustomers(result.data.associatedCustomers || []);
    } catch (err: any) {
      console.error('Error refreshing vendor details:', err);
    }
  };

  const clearCustomerForm = () => {
    setDisplayName('');
    setGivenName('');
    setMiddleName('');
    setFamilyName('');
    setSuffix('');
    setPrimaryPhone('');
    setMobilePhone('');
    setPrimaryEmail('');
    setWebsite('');
    setBillAddrLine1('');
    setBillAddrLine2('');
    setBillAddrCity('');
    setBillAddrCountry('');
    setBillAddrCountrySubDivisionCode('');
    setBillAddrPostalCode('');
    setCurrency('USD');
    setCustomerValidationError(null);
    setCustomerFormError(null);
    setIsDuplicateNameError(false);
  };

  const validateCustomerForm = (): string | null => {
    if (!displayName.trim()) return 'Please enter a company name';

    // Email validation
    if (primaryEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(primaryEmail)) {
        return 'Please enter a valid email address';
      }
    }

    if (!billAddrLine1.trim()) return 'Please enter billing address line 1';
    if (!billAddrCity.trim()) return 'Please enter billing city';
    if (!billAddrCountry.trim()) return 'Please enter billing country';
    if (!billAddrCountrySubDivisionCode.trim()) return 'Please enter billing state/province';
    if (!billAddrPostalCode.trim()) return 'Please enter billing postal code';
    if (!currency) return 'Please select a currency';
    return null;
  };

  const handleCreateCustomerSubmit = async () => {
    // Clear previous errors
    setCustomerValidationError(null);
    setCustomerFormError(null);
    setIsDuplicateNameError(false);

    // Validate form
    const validationErr = validateCustomerForm();
    if (validationErr) {
      setCustomerValidationError(validationErr);
      return;
    }

    setCustomerFormSubmitting(true);

    try {
      const customerData = {
        displayName,
        givenName: givenName || undefined,
        middleName: middleName || undefined,
        familyName: familyName || undefined,
        suffix: suffix || undefined,
        companyName: displayName,
        contact: {
          primaryPhone: primaryPhone || undefined,
          mobilePhone: mobilePhone || undefined,
          primaryEmail: primaryEmail || undefined,
          website: website || undefined
        },
        billingAddress: {
          line1: billAddrLine1,
          line2: billAddrLine2 || undefined,
          city: billAddrCity,
          country: billAddrCountry,
          countrySubDivisionCode: billAddrCountrySubDivisionCode,
          postalCode: billAddrPostalCode
        },
        currencyCode: currency
      };

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}/customers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(customerData)
        }
      );

      if (!response || !response.ok) {
        const result = await response?.json();
        // Check if it's a duplicate name error (409 status)
        if (response?.status === 409) {
          setIsDuplicateNameError(true);
        }
        const errorMessage = result?.message || result?.error || 'Failed to create customer';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      const newCustomerId = result.data?.id;

      // Success - close modal and start refresh process
      setCreateCustomerModalOpen(false);
      clearCustomerForm();
      setCustomerFormSubmitting(false);

      // Show refresh indicator while updating customer list
      setRefreshingCustomers(true);
      try {
        await fetchVendorDetailsForRefresh();

        // Auto-select the newly created customer
        if (newCustomerId) {
          setSelectedCustomerId(newCustomerId);
        }
      } finally {
        setRefreshingCustomers(false);
      }
    } catch (err: any) {
      console.error('Error creating customer:', err);
      setCustomerFormError(err.message || 'Failed to create customer');
      setCustomerFormSubmitting(false);
    }
  };

  const handleCustomerSelectChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    if (value === 'CREATE_NEW') {
      setCreateCustomerModalOpen(true);
    } else {
      setSelectedCustomerId(value);
    }
  };

  // Get selected customer's currency
  const selectedCustomer = associatedCustomers.find(c => c.id === selectedCustomerId);
  const selectedCurrency = selectedCustomer?.currencyCode || 'USD';

  // Group customers by currency code
  const customersByCurrency = associatedCustomers.reduce((acc, customer) => {
    const currency = customer.currencyCode || 'USD';
    if (!acc[currency]) {
      acc[currency] = [];
    }
    acc[currency].push(customer);
    return acc;
  }, {} as Record<string, AssociatedCustomer[]>);

  // Sort currencies descending, then sort customers within each currency by displayName
  const sortedCurrencies = Object.keys(customersByCurrency).sort((a, b) => b.localeCompare(a));
  sortedCurrencies.forEach(currency => {
    customersByCurrency[currency].sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || '')
    );
  });

  // Format billing address for display
  const formatBillingAddress = (billingAddress?: AssociatedCustomer['billingAddress']): string => {
    if (!billingAddress) return 'No address on file';

    const parts: string[] = [];
    if (billingAddress.line1) parts.push(billingAddress.line1);
    if (billingAddress.city || billingAddress.countrySubDivisionCode || billingAddress.postalCode) {
      const cityStateParts = [
        billingAddress.city,
        billingAddress.countrySubDivisionCode,
        billingAddress.postalCode
      ].filter(Boolean);
      if (cityStateParts.length > 0) {
        parts.push(cityStateParts.join(', '));
      }
    }
    if (billingAddress.country) parts.push(billingAddress.country);

    return parts.length > 0 ? parts.join(', ') : 'No address on file';
  };

  // Handle file upload
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadedFile(event.target.files[0]);
    }
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      setUploadedFile(event.dataTransfer.files[0]);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/hub/accounting/vendors/${vendorId}/business`);
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    navigate(`/admin/hub/accounting/vendors/${vendorId}/business`);
  };

  const validateForm = (): string | null => {
    if (!selectedCustomerId) return 'Please select a customer';
    if (!paymentTerms) return 'Please select payment terms';
    if (!product) return 'Please select a product';
    if (!itemDescription.trim()) return 'Please enter an item description';
    if (!itemTotalPrice || parseFloat(itemTotalPrice) <= 0) return 'Please enter a valid item total price';
    if (!subsidiaryInvoiceNumber.trim()) return 'Please enter an invoice number';
    if (!subsidiaryInvoiceAmount || parseFloat(subsidiaryInvoiceAmount) <= 0) return 'Please enter a valid invoice total amount';
    if (!uploadedFile) return 'Please upload a subsidiary invoice document';
    return null;
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setValidationError(null);
    setSubmitError(null);

    // Validate form
    const validationErr = validateForm();
    if (validationErr) {
      setValidationError(validationErr);
      return;
    }

    setSubmitting(true);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('vendorId', vendorId!);
      formData.append('customerId', selectedCustomerId);
      formData.append('paymentTerms', paymentTerms);
      formData.append('product', product);
      formData.append('itemDescription', itemDescription);
      formData.append('itemTotalPrice', itemTotalPrice);
      formData.append('subsidiaryInvoiceNumber', subsidiaryInvoiceNumber);
      formData.append('subsidiaryInvoiceAmount', subsidiaryInvoiceAmount);
      formData.append('currency', 'USD');
      if (uploadedFile) {
        formData.append('file', uploadedFile);
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}/business`,
        {
          method: 'POST',
          body: formData,
          timeout: 60000 // 60 seconds timeout
        }
      );

      if (!response || !response.ok) {
        const result = await response?.json();
        throw new Error(result?.error || 'Failed to add new business');
      }

      // Success - show success modal
      setSuccessModalOpen(true);
    } catch (err: any) {
      console.error('Error submitting business:', err);
      setSubmitError(err.message || 'Failed to add new business');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}`)}
        >
          {vendorName}
        </Link>
        <Typography color="text.primary">Add New Business</Typography>
      </Breadcrumbs>

      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AddBusinessIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Add New Business
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ ml: 7 }}>
          {vendorName}
        </Typography>
      </Box>

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Customer Dropdown */}
          <Grid item xs={12} md={8}>
            <FormControl fullWidth disabled={refreshingCustomers}>
              <InputLabel id="customer-select-label">
                {refreshingCustomers ? 'Refreshing customers...' : 'Customer'}
              </InputLabel>
              <Select
                labelId="customer-select-label"
                id="customer-select"
                value={selectedCustomerId}
                label="Customer"
                onChange={handleCustomerSelectChange}
                startAdornment={
                  refreshingCustomers ? (
                    <CircularProgress size={20} sx={{ ml: 1, mr: 1 }} />
                  ) : null
                }
              >
                {sortedCurrencies.map((currency) => [
                  <ListSubheader key={`header-${currency}`} sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                    Currency: {currency}
                  </ListSubheader>,
                  ...customersByCurrency[currency].map((customer) => (
                    <MenuItem key={customer.id} value={customer.id} sx={{ pl: 4 }}>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <Typography variant="body1">
                            {customer.companyName || customer.displayName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              ml: 2,
                              color: customer.balance === 0 ? 'success.main' :
                                     customer.balance && customer.balance > 0 ? 'error.main' : 'text.primary'
                            }}
                          >
                            {customer.balance !== undefined && customer.balance !== null
                              ? new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: customer.currencyCode || 'USD'
                                }).format(customer.balance)
                              : '—'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatBillingAddress(customer.billingAddress)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ])}
                <MenuItem
                  value="CREATE_NEW"
                  sx={{
                    fontWeight: 600,
                    color: 'rgb(232, 125, 30)',
                    backgroundColor: 'rgba(232, 125, 30, 0.12)',
                    borderTop: '2px solid',
                    borderColor: 'rgb(232, 125, 30)',
                    mt: 1,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(232, 125, 30, 0.25)',
                      color: 'rgb(232, 125, 30)'
                    }
                  }}
                >
                  + Create New Customer
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Terms Dropdown */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="payment-terms-select-label">Payment Terms</InputLabel>
              <Select
                labelId="payment-terms-select-label"
                id="payment-terms-select"
                value={paymentTerms}
                label="Payment Terms"
                onChange={(e: SelectChangeEvent) => setPaymentTerms(e.target.value)}
              >
                <MenuItem value="1">Due on Receipt</MenuItem>
                <MenuItem value="2">Net 15</MenuItem>
                <MenuItem value="3">Net 30</MenuItem>
                <MenuItem value="4">Net 60</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Line Item Details Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Line Item Details
            </Typography>
          </Grid>

          {/* Product Dropdown */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="product-select-label">Product</InputLabel>
              <Select
                labelId="product-select-label"
                id="product-select"
                value={product}
                label="Product"
                onChange={(e: SelectChangeEvent) => setProduct(e.target.value)}
              >
                <MenuItem value="1010000001">Consulting Services</MenuItem>
                <MenuItem value="1010000012">Property Appraisal</MenuItem>
                <MenuItem value="1010000041">Project Expenses</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Item Description */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Item Description"
              value={itemDescription}
              onChange={(e) => setItemDescription(e.target.value)}
            />
          </Grid>

          {/* Item Total Price */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Item Total Price"
              type="number"
              value={itemTotalPrice}
              onChange={(e) => setItemTotalPrice(e.target.value)}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              inputProps={{ step: '0.01' }}
            />
          </Grid>

          {/* Currency Display */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Currency"
              value={selectedCurrency}
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          {/* Subsidiary Invoice Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Subsidiary Invoice Detail
            </Typography>
          </Grid>

          {/* Invoice Number */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Invoice #"
              value={subsidiaryInvoiceNumber}
              onChange={(e) => setSubsidiaryInvoiceNumber(e.target.value)}
            />
          </Grid>

          {/* Invoice Total Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Invoice Total Amount"
              type="number"
              value={subsidiaryInvoiceAmount}
              onChange={(e) => setSubsidiaryInvoiceAmount(e.target.value)}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              inputProps={{ step: '0.01' }}
            />
          </Grid>

          {/* Currency Display */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Currency"
              value="USD"
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          {/* File Upload Box */}
          <Grid item xs={12}>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: 2,
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderStyle: 'dashed',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => document.getElementById('file-upload-input')?.click()}
            >
              <input
                id="file-upload-input"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {uploadedFile ? uploadedFile.name : 'Upload Subsidiary Invoice Document'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {uploadedFile
                  ? 'Click to change file or drag and drop a new one'
                  : 'Drag and drop your file here, or click to browse'
                }
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Supported formats: PDF, PNG, JPG, JPEG
              </Typography>
            </Box>
          </Grid>

          {/* Error Messages */}
          {validationError && (
            <Grid item xs={12}>
              <Alert severity="error">{validationError}</Alert>
            </Grid>
          )}
          {submitError && (
            <Grid item xs={12}>
              <Alert severity="error">{submitError}</Alert>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onClose={handleSuccessModalClose}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>
            New Business added successfully!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessModalClose} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submitting Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={submitting}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">Submitting business...</Typography>
        <Typography variant="body2">This may take a moment</Typography>
      </Backdrop>

      {/* Create New Customer Modal */}
      <Dialog
        open={createCustomerModalOpen}
        onClose={() => {
          if (!customerFormSubmitting) {
            setCreateCustomerModalOpen(false);
            clearCustomerForm();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Customer</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          {customerFormSubmitting && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 1000,
                gap: 2
              }}
            >
              <CircularProgress size={60} />
              <Typography variant="h6">Creating customer...</Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we create the customer
              </Typography>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Identity Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Customer Identity
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Company Name"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  if (isDuplicateNameError) {
                    setIsDuplicateNameError(false);
                  }
                }}
                error={isDuplicateNameError}
                helperText="Required: The name displayed for this customer"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel id="modal-currency-select-label">Customer Invoice Currency</InputLabel>
                <Select
                  labelId="modal-currency-select-label"
                  id="modal-currency-select"
                  value={currency}
                  label="Customer Invoice Currency"
                  onChange={(e: SelectChangeEvent) => setCurrency(e.target.value)}
                >
                  {currencies.map((curr) => (
                    <MenuItem key={curr} value={curr}>
                      {curr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Contact Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Primary Contact / Accounts Payable Contact
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Given Name"
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={1.5}>
              <TextField
                fullWidth
                label="MI"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Family Name"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="Suffix"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                placeholder="Jr., Sr."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Phone"
                value={primaryPhone}
                onChange={(e) => setPrimaryPhone(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile Phone"
                value={mobilePhone}
                onChange={(e) => setMobilePhone(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Primary Email"
                value={primaryEmail}
                onChange={(e) => setPrimaryEmail(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="url"
                label="Website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </Grid>

            {/* Billing Address Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Billing Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Address Line 1"
                value={billAddrLine1}
                onChange={(e) => setBillAddrLine1(e.target.value)}
                helperText="Required: Street address"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={billAddrLine2}
                onChange={(e) => setBillAddrLine2(e.target.value)}
                placeholder="Apt, Suite, Unit, Building"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="City"
                value={billAddrCity}
                onChange={(e) => setBillAddrCity(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="State/Province"
                value={billAddrCountrySubDivisionCode}
                onChange={(e) => setBillAddrCountrySubDivisionCode(e.target.value)}
                helperText="E.g., CA, Ontario, NY"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Postal Code"
                value={billAddrPostalCode}
                onChange={(e) => setBillAddrPostalCode(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Country"
                value={billAddrCountry}
                onChange={(e) => setBillAddrCountry(e.target.value)}
                helperText="E.g., USA, Canada, United Kingdom"
              />
            </Grid>

            {/* Error Messages */}
            {customerValidationError && (
              <Grid item xs={12}>
                <Alert severity="error">{customerValidationError}</Alert>
              </Grid>
            )}
            {customerFormError && (
              <Grid item xs={12}>
                <Alert severity="error">{customerFormError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateCustomerModalOpen(false);
              clearCustomerForm();
            }}
            disabled={customerFormSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomerSubmit}
            variant="contained"
            color="primary"
            disabled={customerFormSubmitting}
          >
            {customerFormSubmitting ? 'Creating...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};